import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import {
  Newspaper, FileText, Sparkles, CheckCircle2, RotateCcw, RefreshCw,
  ExternalLink, Target, Tag, Layers, Rocket, PenLine, Clock, Globe,
} from 'lucide-react'
import { getSession } from '../lib/session'

// Content Studio — the AEO publish loop (draft → approve → live).
//
// The Content Strategist agent turns THIS tenant's real keyword gaps into DRAFT
// pages (status:'draft') in the seo_content_pages store. Nothing a draft says reaches
// the public /discover site until a human approves it here. This surface lists the
// tenant's pages split into "Drafts (pending approval)" and "Live", and drives the
// three write endpoints:
//   POST /seo/pages/:slug/publish    → approve & go live
//   POST /seo/pages/:slug/unpublish  → pull back to draft
//   POST /seo/author-pages           → generate drafts from keyword gaps
// Reads GET /seo/pages/all?status=all. Every render is defensively guarded so a bad or
// missing field degrades to a dash — never [object Object], never a crash.
//
// VISUAL LAYER: the flat "Nunito / Intelligence Briefings" aesthetic (soft palette,
// rounded cards, pills, light + dark tokens) scoped under `.cs-root` so it never leaks.
const API = '/api/v1/marketing-intel'

// ---- small, defensive helpers ---------------------------------------------
// Coerce anything to a safe, human string. Objects never render as [object Object].
const str = (v) => {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return '' // arrays / objects are never printed raw
}
const dash = (v) => {
  const s = str(v).trim()
  return s === '' ? '—' : s
}
const arr = (v) => (Array.isArray(v) ? v : [])
// First present key from an object (rows vary in casing between agent + house pages).
const pick = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return undefined
  for (const k of keys) if (obj[k] != null && obj[k] !== '') return obj[k]
  return undefined
}
// slug-safe path segment for the write endpoints + the /discover link.
const enc = (slug) => encodeURIComponent(str(slug))
// A best-effort "updated 3d ago" — never throws on a bad date.
function ago(v) {
  if (!v) return null
  const d = new Date(v)
  if (isNaN(d.getTime())) return null
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000))
  if (secs < 60) return 'just now'
  const mins = Math.floor(secs / 60); if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60); if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24); if (days < 30) return `${days}d ago`
  const mos = Math.floor(days / 30); if (mos < 12) return `${mos}mo ago`
  return `${Math.floor(mos / 12)}y ago`
}

// ---- scoped stylesheet (Intelligence-Briefings tokens, prefixed .cs-*) -----
const CS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap');
.cs-root{
  --cs-bg:#FBFBF7; --cs-panel:#FFFFFF; --cs-panel2:#F6F6F0; --cs-line:#ECEBE3;
  --cs-ink:#2E3A45; --cs-mut:#7C8794; --cs-dim:#AEB6BD;
  --cs-accent:#4E9AC4; --cs-up:#4FAE5A; --cs-warn:#EAB43C; --cs-purple:#8E7CC3;
  --cs-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  --cs-track:rgba(124,135,148,.14);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--cs-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .cs-root{
  --cs-bg:#0E1116; --cs-panel:#171B22; --cs-panel2:#12161C; --cs-line:#262C36;
  --cs-ink:#E7ECF1; --cs-mut:#95A0AD; --cs-dim:#5C6672;
  --cs-accent:#5FB0DB; --cs-up:#5CC47C; --cs-warn:#EAB43C; --cs-purple:#A796D6;
  --cs-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --cs-track:rgba(149,160,173,.16);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .cs-root{
  --cs-bg:#0E1116; --cs-panel:#171B22; --cs-panel2:#12161C; --cs-line:#262C36;
  --cs-ink:#E7ECF1; --cs-mut:#95A0AD; --cs-dim:#5C6672;
  --cs-accent:#5FB0DB; --cs-up:#5CC47C; --cs-warn:#EAB43C; --cs-purple:#A796D6;
  --cs-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --cs-track:rgba(149,160,173,.16);
}}
.cs-root *{box-sizing:border-box;}
.cs-title{font-size:24px;font-weight:900;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--cs-ink);}
.cs-sub{margin:4px 0 0;color:var(--cs-mut);font-size:14px;font-weight:500;line-height:1.5;}
.cs-card{background:var(--cs-panel);border:1px solid var(--cs-line);border-radius:20px;box-shadow:var(--cs-shadow);padding:18px 20px;}
.cs-sec-h{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--cs-mut);display:flex;align-items:center;gap:8px;margin:0;}
.cs-page-title{font-size:16px;font-weight:800;color:var(--cs-ink);margin:0;line-height:1.35;word-break:break-word;}
.cs-meta{font-size:13px;font-weight:600;line-height:1.5;color:var(--cs-mut);margin:6px 0 0;word-break:break-word;}
.cs-count{font-size:12px;font-weight:800;color:var(--cs-dim);}
.cs-chip{font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;background:var(--cs-panel2);border:1px solid var(--cs-line);color:var(--cs-mut);display:inline-flex;align-items:center;gap:5px;white-space:nowrap;max-width:100%;overflow:hidden;text-overflow:ellipsis;}
.cs-pill{font-size:11px;font-weight:800;padding:4px 11px;border-radius:20px;display:inline-flex;align-items:center;gap:5px;white-space:nowrap;}
.cs-btn{font-family:Nunito,system-ui,sans-serif;font-size:13px;font-weight:800;border-radius:12px;padding:10px 16px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;border:1px solid var(--cs-line);background:var(--cs-panel);color:var(--cs-ink);transition:.16s;box-shadow:var(--cs-shadow);white-space:nowrap;}
.cs-btn:hover:not(:disabled){transform:translateY(-1px);}
.cs-btn:disabled{opacity:.55;cursor:default;transform:none;}
.cs-btn.primary{background:var(--cs-accent);color:#fff;border-color:transparent;}
.cs-btn.go{background:var(--cs-up);color:#fff;border-color:transparent;}
.cs-btn.ghost{background:transparent;}
.cs-spin{animation:cs-spin 1s linear infinite;}
@keyframes cs-spin{to{transform:rotate(360deg);}}
.cs-fade{animation:cs-fade .35s ease both;}
@keyframes cs-fade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
.cs-link{color:var(--cs-accent);font-weight:800;text-decoration:none;display:inline-flex;align-items:center;gap:5px;}
.cs-link:hover{text-decoration:underline;}
.cs-row{display:flex;gap:14px;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;}
.cs-tags{display:flex;flex-wrap:wrap;gap:7px;margin-top:12px;}
`

let _csStyleInjected = false
function CsStyle() {
  useEffect(() => {
    if (_csStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-cs-style', '1')
    el.textContent = CS_CSS
    document.head.appendChild(el)
    _csStyleInjected = true
  }, [])
  return null
}

const TONE = {
  up:   { c: 'var(--cs-up)',     bg: 'rgba(79,174,90,.15)' },
  warn: { c: 'var(--cs-warn)',   bg: 'rgba(234,180,60,.16)' },
  acc:  { c: 'var(--cs-accent)', bg: 'rgba(78,154,196,.14)' },
  pur:  { c: 'var(--cs-purple)', bg: 'rgba(142,124,195,.16)' },
  mut:  { c: 'var(--cs-mut)',    bg: 'rgba(124,135,148,.14)' },
}
const pill = (t) => ({ background: t.bg, color: t.c })

// ---- one page card ---------------------------------------------------------
// mode: 'draft' → shows Approve & Publish; 'live' → shows Unpublish + Discover link.
function PageCard({ page, mode, busy, onPublish, onUnpublish }) {
  const slug = pick(page, ['slug'])
  const title = pick(page, ['title', 'h1'])
  const meta = pick(page, ['metaDescription', 'description'])
  const keyword = pick(page, ['targetKeyword', 'keyword'])
  const cluster = pick(page, ['cluster'])
  const intent = pick(page, ['intent'])
  const source = pick(page, ['source'])
  const updated = ago(pick(page, ['updatedAt', 'createdAt']))
  const isLive = mode === 'live'
  const acting = busy === slug

  return (
    <div className="cs-card cs-fade">
      <div className="cs-row">
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="cs-pill" style={pill(isLive ? TONE.up : TONE.warn)}>
              {isLive ? <><CheckCircle2 size={12} /> Live</> : <><PenLine size={12} /> Draft</>}
            </span>
            {updated && (
              <span className="cs-count" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Clock size={11} /> {updated}
              </span>
            )}
          </div>
          <p className="cs-page-title" style={{ marginTop: 10 }}>{dash(title)}</p>
          {meta && <p className="cs-meta">{str(meta)}</p>}

          <div className="cs-tags">
            <span className="cs-chip" title="URL slug"><Globe size={11} /> /discover/{dash(slug)}</span>
            {keyword && <span className="cs-chip" title="Target keyword"><Target size={11} /> {str(keyword)}</span>}
            {cluster && <span className="cs-chip" title="Cluster"><Layers size={11} /> {str(cluster)}</span>}
            {intent && <span className="cs-chip" title="Search intent"><Tag size={11} /> {str(intent)}</span>}
            {source && <span className="cs-chip" title="Authored by">{str(source) === 'agent' ? <Sparkles size={11} /> : <FileText size={11} />} {str(source)}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch', flex: 'none' }}>
          {isLive ? (
            <>
              <a
                className="cs-btn ghost"
                href={`/discover/${enc(slug)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View live <ExternalLink size={14} />
              </a>
              <button className="cs-btn" disabled={acting || !slug} onClick={() => onUnpublish(slug)}>
                {acting ? <RefreshCw size={14} className="cs-spin" /> : <RotateCcw size={14} />}
                {acting ? 'Working…' : 'Unpublish'}
              </button>
            </>
          ) : (
            <button className="cs-btn go" disabled={acting || !slug} onClick={() => onPublish(slug)}>
              {acting ? <RefreshCw size={14} className="cs-spin" /> : <Rocket size={14} />}
              {acting ? 'Publishing…' : 'Approve & Publish'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="cs-card" style={{ textAlign: 'center', padding: '36px 22px' }}>
      <Icon size={26} style={{ color: 'var(--cs-dim)', margin: '0 auto 10px' }} />
      <p style={{ fontWeight: 800, color: 'var(--cs-ink)', margin: '0 0 4px', fontSize: 15 }}>{title}</p>
      {body && <p className="cs-sub" style={{ maxWidth: 420, margin: '0 auto' }}>{body}</p>}
    </div>
  )
}

export default function ContentStudio() {
  const session = getSession()
  const nsReady = !!session?.namespaceId

  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [authoring, setAuthoring] = useState(false)
  const [busySlug, setBusySlug] = useState(null) // slug currently publishing/unpublishing

  const load = useCallback(async () => {
    if (!nsReady) { setLoading(false); return }
    setLoading(true); setError(false)
    try {
      const r = await axios.get(`${API}/seo/pages/all`, { params: { status: 'all' } })
      const list = arr(r?.data?.pages)
      setPages(list)
    } catch (_) {
      setError(true)
      setPages([])
    } finally {
      setLoading(false)
    }
  }, [nsReady])

  useEffect(() => { load() }, [load])

  const publish = useCallback(async (slug) => {
    if (!slug) return
    setBusySlug(slug)
    try {
      await axios.post(`${API}/seo/pages/${enc(slug)}/publish`)
      toast.success('Published — the page is now live on /discover')
      await load()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not publish this page')
    } finally {
      setBusySlug(null)
    }
  }, [load])

  const unpublish = useCallback(async (slug) => {
    if (!slug) return
    setBusySlug(slug)
    try {
      await axios.post(`${API}/seo/pages/${enc(slug)}/unpublish`)
      toast.success('Unpublished — moved back to drafts')
      await load()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not unpublish this page')
    } finally {
      setBusySlug(null)
    }
  }, [load])

  const authorFromGaps = useCallback(async () => {
    setAuthoring(true)
    try {
      const r = await axios.post(`${API}/seo/author-pages`, { limit: 5 })
      const n = Number(r?.data?.authored)
      toast.success(Number.isFinite(n) && n > 0
        ? `Drafted ${n} page${n === 1 ? '' : 's'} from your keyword gaps`
        : 'No new drafts — no fresh keyword gaps to write for yet')
      await load()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not draft pages right now')
    } finally {
      setAuthoring(false)
    }
  }, [load])

  const drafts = pages.filter((p) => str(pick(p, ['status'])) !== 'published')
  const live = pages.filter((p) => str(pick(p, ['status'])) === 'published')

  return (
    <div className="cs-root space-y-8">
      <CsStyle />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="cs-title">
            <Newspaper size={24} style={{ color: 'var(--cs-accent)' }} /> Content Studio
          </h2>
          <p className="cs-sub">
            Review AEO pages the Content Strategist drafts from your real keyword gaps, then approve them live to /discover. Nothing publishes until you say so.
          </p>
        </div>
        {nsReady && (
          <button onClick={authorFromGaps} disabled={authoring} className="cs-btn primary">
            {authoring ? <RefreshCw size={15} className="cs-spin" /> : <Sparkles size={15} />}
            {authoring ? 'Drafting…' : 'Draft pages from my keyword gaps'}
          </button>
        )}
      </div>

      {!nsReady && (
        <EmptyState icon={Newspaper} title="Connect a workspace to activate Content Studio"
          body="Content Studio manages your tenant's AEO pages. Open a workspace to start drafting and publishing." />
      )}

      {nsReady && loading && (
        <div className="cs-card cs-sub" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading your pages…</div>
      )}

      {nsReady && !loading && error && (
        <EmptyState icon={FileText} title="Couldn't load your pages"
          body="Something went wrong reaching the page store. Try drafting from your keyword gaps, or refresh the page." />
      )}

      {nsReady && !loading && !error && (
        <>
          {/* DRAFTS — pending approval */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="cs-sec-h"><PenLine size={14} /> Drafts (pending approval) <span className="cs-count">· {drafts.length}</span></h3>
            </div>
            {drafts.length === 0 ? (
              <EmptyState icon={PenLine} title="No drafts waiting"
                body="Click “Draft pages from my keyword gaps” and the Content Strategist will write new AEO pages here for your review." />
            ) : (
              <div className="space-y-3">
                {drafts.map((p, i) => (
                  <PageCard key={pick(p, ['slug']) || i} page={p} mode="draft"
                    busy={busySlug} onPublish={publish} onUnpublish={unpublish} />
                ))}
              </div>
            )}
          </section>

          {/* LIVE */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="cs-sec-h"><CheckCircle2 size={14} /> Live <span className="cs-count">· {live.length}</span></h3>
            </div>
            {live.length === 0 ? (
              <EmptyState icon={Globe} title="Nothing live yet"
                body="Approved pages appear here and go straight to your public /discover site. Approve a draft above to publish your first one." />
            ) : (
              <div className="space-y-3">
                {live.map((p, i) => (
                  <PageCard key={pick(p, ['slug']) || i} page={p} mode="live"
                    busy={busySlug} onPublish={publish} onUnpublish={unpublish} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
